import {
  DecodedSelectionID,
  Selection,
  SportEvent,
  DecodedMarketID,
} from "@/types/SportsApiResponses";
import {
  BetslipCategory,
  BetslipSelectionAction,
  BetslipState,
} from "./betslipTypes";

export const encodeSelectionId = (decodedId: DecodedSelectionID): string => {
  const parts = [
    decodedId.providerId,
    decodedId.matchId,
    decodedId.eventId,
    decodedId.hash,
    decodedId.group,
    decodedId.type,
  ];

  if (decodedId.param != null) {
    parts.push(decodedId.param);
  }

  return parts.join("_");
};

export const decodeSelectionId = (
  selection: Pick<Selection, "id">
): DecodedSelectionID => {

  const [providerId, matchId, eventId, hash, group, type, param] =
    selection.id.split("_");

  return {
    providerId: Number(providerId),
    matchId: Number(matchId),
    eventId: Number(eventId),
    group: Number(group),
    type: Number(type),
    hash,
    param: param !== undefined ? param : null,
  };
};

export const encodeMarketId = (props: DecodedMarketID): string => {
  const parts = [
    props.providerId,
    props.matchId,
    props.eventId,
    props.group,
    props.hash,
  ];
  return parts.join("_");
};

export const decodeMarketId = (id: string): DecodedMarketID => {

  const [providerId, matchId, eventId, group, hash] = id.split("_");

  return {
    providerId: Number(providerId),
    matchId: Number(matchId),
    eventId: Number(eventId),
    group: Number(group),
    hash,
  };
};

/**
 * Gets selection event id by decoding the selection id
 */
export const getSelectionEventId = (
  selection: Pick<Selection, "id">
): number => {
  const decodedId = decodeSelectionId(selection);
  return decodedId.eventId;
};

/**
 * Gets selection match id by decoding the selection id
 */
export const getSelectionMatchId = (
  selection: Pick<Selection, "id">
): number => {
  const decodedId = decodeSelectionId(selection);
  return decodedId.matchId;
};

/**
 * Generates selection market id by decoding the selection id
 */
export const getSelectionMarketId = (selection: Pick<Selection, "id">) => {
  return encodeMarketId(decodeSelectionId(selection));
};

const syncSelectionsByEvent = (state: BetslipState, selection: Selection) => {
  const eventId = getSelectionEventId(selection);
  const selectionsByEvent = state.selectionsByEvent[eventId];

  if (selectionsByEvent) {
    const index = selectionsByEvent.findIndex((id) => id === selection.id);
    if (index === -1) selectionsByEvent.push(selection.id);
  } else {
    state.selectionsByEvent[eventId] = [selection.id];
  }
};

const syncSelectionsByMarket = (state: BetslipState, selection: Selection) => {
  const marketId = getSelectionMarketId(selection);

  const selectionsByMarket = state.selectionsByMarket[marketId];
  if (selectionsByMarket) {
    const index = selectionsByMarket.findIndex((id) => id === selection.id);
    if (index === -1) selectionsByMarket.push(selection.id);
  } else {
    state.selectionsByMarket[marketId] = [selection.id];
  }
};

const addSelection = (
  state: BetslipState,
  selection: Selection,
  event: SportEvent
) => {
  state.eventsMap[event.id] = event;

  const eventIndex = state.events.findIndex((id) => id === event.id);
  if (eventIndex === -1) state.events.push(event.id);

  state.originalOddsMap[selection.id] = selection.odds;
  state.selectionMap[selection.id] = selection;
  state.selections.push(selection.id);
};

const onSelectionAdd = (state: BetslipState, selection: Selection) => {
  state.category = getNewCategory(state, "add");
  cleanSelectionRelatedStakes(state, selection, "add");
  syncSelectionsByEvent(state, selection);
  syncSelectionsByMarket(state, selection);
};

const cleanSelectionEventRelation = (
  state: BetslipState,
  selection: Selection
) => {
  const eventId = getSelectionEventId(selection);
  const selectionsByEvent = state.selectionsByEvent[eventId];
  if (!selectionsByEvent) return;

  const selectionIndex = selectionsByEvent.findIndex(
    (id) => id === selection.id
  );
  if (selectionIndex > -1) {
    selectionsByEvent.splice(selectionIndex, 1);
  }

  if (selectionsByEvent.length === 0) {
    const eventIndex = state.events.findIndex((id) => id === eventId);
    if (eventIndex > -1) state.events.splice(eventIndex, 1);

    delete state.eventsMap[eventId];
    delete state.selectionsByEvent[eventId];
  }
};

const cleanSelectionMarketRelation = (
  state: BetslipState,
  selection: Selection
) => {
  const marketId = getSelectionMarketId(selection);

  const selectionsByMarket = state.selectionsByMarket[marketId];
  if (!selectionsByMarket) return;

  const selectionIndex = selectionsByMarket.findIndex(
    (id) => id === selection.id
  );
  if (selectionIndex > -1) selectionsByMarket.splice(selectionIndex, 1);

  if (selectionsByMarket.length === 0) {
    delete state.selectionsByMarket[marketId];
  }
};

const deleteSelection = (state: BetslipState, selectionId: string) => {
  const index = state.selections.findIndex((id) => id === selectionId);
  if (index > -1) state.selections.splice(index, 1);

  delete state.selectionMap[selectionId];
  delete state.originalOddsMap[selectionId];
};

const cleanSelectionRelatedStakes = (
  state: BetslipState,
  selection: Selection,
  action: BetslipSelectionAction
) => {
  state.stakesMap.system = {};
  state.systemComboStake = "";

  if (action === "delete") {
    delete state.stakesMap.single[selection.id];
  }
};

const onSelectionDelete = (state: BetslipState, selection: Selection) => {
  state.category = getNewCategory(state, "delete");

  cleanSelectionRelatedStakes(state, selection, "delete");
  cleanSelectionEventRelation(state, selection);
  cleanSelectionMarketRelation(state, selection);
};

const hasMaxSelectionsCount = (state: BetslipState) => {
  const settings = state.settingsMap[state.category];
  if (settings) {
    return state.selections.length >= settings.maxSelectionsCount;
  }
};

const getNewCategory = (
  state: BetslipState,
  action: BetslipSelectionAction
) => {
  const { category: tab, selections } = state;

  const count = selections.length;

  if (action === "add" && tab === BetslipCategory.Single && count >= 2) {
    return BetslipCategory.Combination;
  }

  if (action === "delete" && tab !== BetslipCategory.Single) {
    if (count === 2) return BetslipCategory.Combination;
    if (count < 2) return BetslipCategory.Single;
  }

  return tab;
};

const deleteEventSelections = (state: BetslipState, eventId: number) => {
  const selectionsByEvent = state.selectionsByEvent[eventId];
  if (!selectionsByEvent) return;

  // Create a copy of the array to iterate over
  const selectionsToDelete = [...selectionsByEvent];

  for (const id of selectionsToDelete) {
    const selection = state.selectionMap[id];
    deleteSelection(state, id);
    onSelectionDelete(state, selection);
  }
};

export {
  deleteEventSelections,
  cleanSelectionRelatedStakes,
  hasMaxSelectionsCount,
  getNewCategory,
  addSelection,
  onSelectionAdd,
  deleteSelection,
  onSelectionDelete,
};
